import axios, { AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { trackApiFailure } from 'monitoring/utils';
import { getSafeErrorMessage } from 'utils/errorUtils';

import { AuthService, MFAService } from '@yubi/yb-module-auth';

import { store } from '../../redux/store/store';
import sessionStorageService from '../storage/sessionStorageService';

/**
 * Generates a random hexadecimal string of specified length
 * @param length The length of the hexadecimal string to generate
 * @returns A random hexadecimal string
 */
function generateRandomHex(length: number): string {
  return [...Array(length)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join('');
}

/**
 * Generates a traceparent header value with unique trace ID for each request
 * @returns A properly formatted traceparent header value
 */
function generateTraceparent(): string {
  const version = '00';
  const traceId = generateRandomHex(32); // Generate a new trace ID for each request
  const parentId = generateRandomHex(16);
  const flags = '01'; // 01 indicates the trace should be sampled

  return `${version}-${traceId}-${parentId}-${flags}`;
}

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const userId = AuthService.getUserId();
    const userName = state.userConfiguration.caUserName;
    const entityId = state.userConfiguration.entityId; // Adjust the path to the EntityId according to your store structure
    const currentEntityId =
      state.userConfiguration.entityId || Cookies.get('Current-Entity-Id');

    // Modify the request config as needed
    const jwtToken = AuthService.getAccessToken();
    const mfaToken = MFAService.getMFAToken();

    // updated the below headers for lender API's
    const userGroup = state.userConfiguration.userGroup;
    const userRole = state.userConfiguration.userRole;
    const currentGroup = userGroup;

    const headers = config.headers as AxiosRequestHeaders;
    headers['User-Id'] = userId;
    headers['Entity-Id'] = entityId || currentEntityId;
    headers['Current-Entity-Id'] = currentEntityId;
    headers['Current-Group'] = currentGroup;
    headers.Group = userGroup || currentGroup;
    headers['User-Role'] = userRole;
    headers['Mfa-Token'] = mfaToken;
    headers.Authorization = `Bearer ${jwtToken}`;
    headers['User-Name'] = userName;
    headers['Allocator-Id'] = state.userConfiguration.lenderId;
    headers['Lender-Id'] = state.userConfiguration.lenderId;
    // headers['Agent-Id'] = userId;
    headers.traceparent = generateTraceparent();

    (config as any).metadata = { startTime: Date.now() };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors and track API failures
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Track API failure in Kfuse monitoring
    const statusCode = error.response?.status || 0;
    const endpoint = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const errorResponse = error.response?.data; // Full API error response
    const startTime = error.config?.metadata?.startTime;
    const duration = startTime ? Date.now() - startTime : undefined;
    const errorMessage =
      errorResponse?.message ||
      errorResponse?.error ||
      error.message ||
      'Unknown error';

    // Capture request details for debugging
    const requestDetails = {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params,
      data: error.config?.data, // Request body
      headers: {
        traceparent: error.config?.headers?.traceparent,
        'Entity-Id': error.config?.headers?.['Entity-Id'],
        'User-Id': error.config?.headers?.['User-Id'],
      },
    };

    trackApiFailure({
      endpoint,
      method,
      statusCode,
      errorMessage,
      errorResponse,
      requestDetails,
      traceId: error.config?.headers?.traceparent,
      duration,
    });

    // Handle 401 Unauthorized error
    if (error.response && error.response.status === 401) {
      console.error('Unauthorized error');
      sessionStorageService.clearSessionStorage();
      store.dispatch({ type: 'RESET' });
      AuthService.logout();
      AuthService.logoutUser();
    }

    (error as any).safeMessage = getSafeErrorMessage(
      errorResponse?.message || errorResponse?.error || error.message
    );

    return Promise.reject(error);
  }
);

export default axiosInstance;
